import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, TrendingUp, DollarSign, Users, ExternalLink, ArrowUp, ArrowDown } from "lucide-react";
import AFFILIATE_LINKS, { calculatePotentialCommission, getHighPriorityServices, getDFWLocalServices } from "@/config/affiliateLinks";

/**
 * AFFILIATE ROI DASHBOARD
 * For site owner to track affiliate performance and revenue potential
 * 
 * Access this page at: /affiliate-roi
 * (Add password protection in production)
 */
const AffiliateROI = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalListings: 0,
    activeListings: 0,
    projectedMonthly: 0,
    projectedAnnual: 0
  });

  useEffect(() => {
    // In production, fetch real data from backend
    // For now, using placeholder calculations
    const mockListings = 25; // Replace with real count
    
    calculateProjectedRevenue(mockListings);
  }, []);

  const calculateProjectedRevenue = (listingCount) => {
    // Conservative conversion rate assumptions
    const conversionRates = {
      staging: 0.30, // 30% of sellers use staging
      cleaning: 0.60, // 60% use cleaning
      moving: 0.50, // 50% use movers
      surveying: 0.80, // 80% need surveys (required for most)
      mortgage: 0.40, // 40% of buyers need financing
      insurance: 0.70, // 70% of buyers need insurance
      inspection: 0.85 // 85% of buyers get inspections
    };

    let monthlyTotal = 0;

    // Calculate staging revenue
    const stagingServices = Object.values(AFFILIATE_LINKS.staging);
    stagingServices.forEach(service => {
      const referrals = listingCount * conversionRates.staging;
      monthlyTotal += referrals * service.commissionAmount;
    });

    // Cleaning
    const cleaningServices = Object.values(AFFILIATE_LINKS.cleaning);
    cleaningServices.forEach(service => {
      const referrals = listingCount * conversionRates.cleaning;
      monthlyTotal += referrals * service.commissionAmount;
    });

    // Moving
    const movingServices = Object.values(AFFILIATE_LINKS.moving);
    movingServices.forEach(service => {
      const referrals = listingCount * conversionRates.moving;
      monthlyTotal += referrals * service.commissionAmount;
    });

    // Surveying
    const surveyingServices = Object.values(AFFILIATE_LINKS.surveying);
    surveyingServices.forEach(service => {
      const referrals = listingCount * conversionRates.surveying;
      monthlyTotal += referrals * service.commissionAmount;
    });

    // Mortgage
    const mortgageServices = Object.values(AFFILIATE_LINKS.mortgage);
    mortgageServices.forEach(service => {
      const referrals = listingCount * conversionRates.mortgage;
      monthlyTotal += referrals * service.commissionAmount;
    });

    // Insurance
    const insuranceServices = Object.values(AFFILIATE_LINKS.insurance);
    insuranceServices.forEach(service => {
      const referrals = listingCount * conversionRates.insurance;
      monthlyTotal += referrals * service.commissionAmount;
    });

    setStats({
      totalListings: listingCount,
      activeListings: listingCount,
      projectedMonthly: monthlyTotal,
      projectedAnnual: monthlyTotal * 12
    });
  };

  const allServices = [];
  Object.keys(AFFILIATE_LINKS).forEach(category => {
    Object.entries(AFFILIATE_LINKS[category]).forEach(([key, service]) => {
      allServices.push({
        ...service,
        key: `${category}.${key}`
      });
    });
  });

  // Sort by commission amount (highest first)
  allServices.sort((a, b) => b.commissionAmount - a.commissionAmount);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900">Affiliate ROI Dashboard</span>
                <p className="text-xs text-gray-500">Owner Access Only</p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 opacity-80" />
              <ArrowUp className="w-5 h-5" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{stats.activeListings}</h3>
            <p className="text-blue-100 text-sm">Active Listings</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 opacity-80" />
              <TrendingUp className="w-5 h-5" />
            </div>
            <h3 className="text-3xl font-bold mb-1">${stats.projectedMonthly.toLocaleString(undefined, {maximumFractionDigits: 0})}</h3>
            <p className="text-green-100 text-sm">Projected Monthly</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 opacity-80" />
              <TrendingUp className="w-5 h-5" />
            </div>
            <h3 className="text-3xl font-bold mb-1">${stats.projectedAnnual.toLocaleString(undefined, {maximumFractionDigits: 0})}</h3>
            <p className="text-purple-100 text-sm">Projected Annual</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <ArrowUp className="w-5 h-5" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{allServices.length}</h3>
            <p className="text-orange-100 text-sm">Active Partners</p>
          </Card>
        </div>

        {/* Affiliate Setup Status */}
        <Card className="p-6 mb-8 bg-amber-50 border-2 border-amber-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🔧 Setup Checklist</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-amber-400 rounded flex items-center justify-center text-white font-bold">!</div>
              <span className="text-gray-700">
                <strong>Action Required:</strong> Replace "YOUR_AFFILIATE_ID" placeholders in <code className="bg-amber-100 px-2 py-1 rounded">/app/frontend/src/config/affiliateLinks.js</code>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-amber-400 rounded flex items-center justify-center text-white font-bold">!</div>
              <span className="text-gray-700">
                Sign up for affiliate programs - see <code className="bg-amber-100 px-2 py-1 rounded">/app/AFFILIATE_PROGRAMS_DIRECTORY.md</code>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-amber-400 rounded flex items-center justify-center text-white font-bold">!</div>
              <span className="text-gray-700">
                Test all affiliate links after updating IDs
              </span>
            </div>
          </div>
        </Card>

        {/* All Services Table */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">All Affiliate Partners</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Commission</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Avg $/Referral</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Priority</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Local</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Link Status</th>
                </tr>
              </thead>
              <tbody>
                {allServices.map((service, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{service.name}</span>
                        {service.priority === 1 && (
                          <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full">⭐ Top</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 capitalize">{service.category}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 font-medium">{service.commission}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className="text-green-700 font-bold">${service.commissionAmount}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">P{service.priority}</td>
                    <td className="py-3 px-4">
                      {service.local ? (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">DFW</span>
                      ) : (
                        <span className="text-gray-400 text-xs">National</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {service.url.includes('YOUR_AFFILIATE_ID') ? (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">❌ Not Set</span>
                      ) : (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">✅ Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Performance Tracking (Coming Soon)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Track clicks, conversions, and revenue from each affiliate partner. 
              Integrate with Google Analytics and affiliate network dashboards.
            </p>
            <Button variant="outline" disabled>
              Coming in v2.0
            </Button>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">✉️ Email Campaigns (Coming Soon)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Automated email sequences recommending services based on listing stage. 
              Drip campaigns to maximize affiliate conversions.
            </p>
            <Button variant="outline" disabled>
              Coming in v2.0
            </Button>
          </Card>
        </div>

        {/* Revenue Calculator */}
        <Card className="p-6 mt-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">💰 Revenue Calculator</h2>
          <p className="text-sm text-gray-600 mb-6">
            Based on your current {stats.activeListings} active listings and conservative conversion rates:
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <p className="text-sm text-gray-600 mb-1">With 50 listings/month</p>
              <p className="text-3xl font-bold text-green-700">${(stats.projectedMonthly * 2).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-gray-500 mt-1">Monthly Revenue</p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <p className="text-sm text-gray-600 mb-1">With 100 listings/month</p>
              <p className="text-3xl font-bold text-green-700">${(stats.projectedMonthly * 4).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-gray-500 mt-1">Monthly Revenue</p>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <p className="text-sm text-gray-600 mb-1">With 200 listings/month</p>
              <p className="text-3xl font-bold text-green-700">${(stats.projectedMonthly * 8).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-gray-500 mt-1">Monthly Revenue</p>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-lg p-4 border border-green-200">
            <p className="text-sm text-gray-700">
              <strong>Pro Tip:</strong> Focus on high-commission services (surveying, mortgage, staging) and DFW local partners 
              for best conversion rates. As your listing count grows, this passive income scales automatically.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateROI;
